/**
 * Pure helpers for Getting started: the facts a checklist is derived from, reading other
 * modules' answers (each optional), and choosing where a first mission runs.
 */
import { MISSION_STATE_LABEL, isPhoneDevice } from '@orgus/protocol';
import type { Checkout, Machine, MissionTemplate, MissionView, OnboardingFacts, OnboardingStepId, Project, Session } from '@orgus/protocol';

/** What the optional modules said; null while unknown, `installed: false` when the hub does not have them. */
export interface ModuleProbes {
  auth: AuthProbe | null;
  missions: { installed: boolean; count: number } | null;
  notify: NotifyProbe | null;
}

export interface AuthProbe {
  installed: boolean;
  githubConfigured: boolean;
  /** Who this browser is signed in as, when the auth module says. */
  me: { id: string; role: string; via: 'owner-link' | 'github' | 'token'; name: string | null } | null;
}

export interface NotifyProbe {
  installed: boolean;
  /** Push channels registered from a phone or tablet. */
  phones: { name: string; device: string }[];
  /** Every enabled channel, phones included, as the person would name it ('Safari on iPhone', 'Slack #ops'). */
  channels: string[];
  /** The https address notifications link to, when set. */
  publicUrl: string | null;
}

export const NO_PROBES: ModuleProbes = { auth: null, missions: null, notify: null };

interface EntitiesLike {
  machines: Record<string, Machine>;
  projects: Record<string, Project>;
  sessions: Record<string, Session>;
  suggestions: Record<string, unknown>;
}

export function onboardingFacts(e: EntitiesLike, p: ModuleProbes): OnboardingFacts {
  const machines = Object.values(e.machines);
  let launched = 0;
  for (const s of Object.values(e.sessions)) if (s.origin === 'orgus') launched++;
  return {
    machinesOnline: machines.filter((m) => m.online).length,
    machinesTotal: machines.length,
    projects: Object.keys(e.projects).length,
    suggestions: Object.keys(e.suggestions).length,
    launchedSessions: launched,
    missions: p.missions?.count ?? 0,
    phoneDevices: p.notify?.phones.length ?? 0,
    channels: p.notify?.channels.length ?? 0,
  };
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** GET /api/auth/providers and GET /api/me from the auth module, whatever extra fields they carry. */
export function readAuth(providers: unknown, me: unknown): AuthProbe {
  const github = isObj(providers) && isObj(providers.github) && providers.github.configured === true;
  let who: AuthProbe['me'] = null;
  if (isObj(me) && isObj(me.actor) && typeof me.actor.id === 'string') {
    const via = me.via === 'github' || me.via === 'token' ? me.via : 'owner-link';
    const member = isObj(me.member) ? me.member : null;
    const name = member && typeof member.name === 'string' && member.name ? member.name : member && typeof member.login === 'string' ? member.login : null;
    who = { id: me.actor.id, role: typeof me.actor.role === 'string' ? me.actor.role : 'owner', via, name };
  }
  return { installed: true, githubConfigured: github, me: who };
}

/** GET /api/missions from the missions module. */
export function readMissions(v: unknown): { installed: boolean; count: number } {
  return { installed: true, count: isObj(v) && Array.isArray(v.missions) ? v.missions.length : 0 };
}

const WEBHOOK_LABEL: Record<string, string> = { slack: 'Slack', teams: 'Teams' };

/** How a channel reads in a list: a push channel by its device, others by kind and name. */
function channelLabel(c: Record<string, unknown>): string {
  const name = typeof c.name === 'string' ? c.name.trim() : '';
  if (c.kind === 'push') return typeof c.device === 'string' && c.device ? c.device : name || 'Push';
  const kind = c.kind === 'email' ? 'Email' : (WEBHOOK_LABEL[typeof c.format === 'string' ? c.format : ''] ?? 'Webhook');
  return name && name.toLowerCase() !== kind.toLowerCase() ? `${kind} ${name}` : kind;
}

/** GET /api/notify from the notify module: enabled channels, phones that receive push, and the public address. */
export function readNotify(v: unknown): NotifyProbe {
  const phones: NotifyProbe['phones'] = [];
  const channels: string[] = [];
  let publicUrl: string | null = null;
  if (isObj(v)) {
    for (const c of Array.isArray(v.channels) ? v.channels : []) {
      if (!isObj(c) || c.enabled === false || (c.kind !== 'push' && c.kind !== 'webhook' && c.kind !== 'email')) continue;
      channels.push(channelLabel(c));
      if (c.kind !== 'push') continue;
      const device = typeof c.device === 'string' ? c.device : '';
      if (isPhoneDevice(device)) phones.push({ name: typeof c.name === 'string' ? c.name : device, device });
    }
    if (isObj(v.settings) && typeof v.settings.publicUrl === 'string' && v.settings.publicUrl) publicUrl = v.settings.publicUrl;
  }
  return { installed: true, phones, channels, publicUrl };
}

/** The machine the hub runs on: the one whose host name matches, else the only one. */
export function thisMachine(machines: Machine[], hostname: string | null | undefined): Machine | undefined {
  const h = (hostname ?? '').toLowerCase();
  const match = h ? machines.find((m) => m.hostname.toLowerCase() === h || m.hostname.toLowerCase().split('.')[0] === h.split('.')[0]) : undefined;
  return match ?? (machines.length === 1 ? machines[0] : undefined);
}

export interface RunTarget {
  machineId: string;
  checkoutId: string;
}

/**
 * Where a mission for this project runs by default: its main checkout on an online machine,
 * preferring this machine, else any checkout on an online machine. Null when none is online.
 */
export function defaultTarget(projectId: string, checkouts: Checkout[], machines: Record<string, Machine>, preferMachineId?: string): RunTarget | null {
  const online = checkouts.filter((c) => c.projectId === projectId && machines[c.machineId]?.online);
  const rank = (c: Checkout) => (c.machineId === preferMachineId ? 0 : 2) + (c.kind === 'main' ? 0 : 1);
  const best = [...online].sort((a, b) => rank(a) - rank(b) || a.path.localeCompare(b.path))[0];
  return best ? { machineId: best.machineId, checkoutId: best.id } : null;
}

/** The label of the main action on the checklist: what the next step asks of you. */
export const NEXT_ACTION: Record<OnboardingStepId, string> = {
  signin: 'Review sign-in',
  machine: 'Connect this machine',
  repo: 'Connect a repo',
  mission: 'Start a first mission',
  phone: 'Pick a notification channel',
};

/** One line for a template card: its summary, else its goal with each {field} read as the field's name. */
export function describeTemplate(t: Pick<MissionTemplate, 'summary' | 'goal' | 'fields'>): string {
  if (t.summary.trim()) return t.summary;
  return t.goal.replace(/\{([a-z][a-z0-9_]*)\}/g, (all, key: string) => t.fields.find((f) => f.key === key)?.label.toLowerCase() ?? all);
}

/**
 * Where a first mission stands once started: running only when its session has reported from
 * the machine, starting until then. Null while the hub has not listed the mission yet.
 */
export function firstMissionLine(view: Pick<MissionView, 'state' | 'sessions'> | null, machine: string): string | null {
  if (!view) return null;
  if (view.state === 'running') return view.sessions.some((s) => s.session) ? `Running on ${machine}` : `Starting on ${machine}`;
  return MISSION_STATE_LABEL[view.state];
}

/** Templates in the order the mission step shows them: built-ins first, then the hub's own by title. */
export function orderTemplates(list: MissionTemplate[]): MissionTemplate[] {
  return [...list].sort((a, b) => Number(b.builtIn) - Number(a.builtIn) || (a.builtIn ? 0 : a.title.localeCompare(b.title)));
}
